import React, { useState, useEffect, useRef } from "react";
import { FaCheckCircle, FaUserTie, FaCogs } from "react-icons/fa";
import Reveal from "../../components/Reveal";
import { useDarkMode } from "../../hooks/useDarkMode";

const HomeSectionData = () => {
  const { isDarkMode } = useDarkMode();

  const [circlePos, setCirclePos] = useState({ x: 0, y: 0 });
  const [years, setYears] = useState(0);
  const [scale, setScale] = useState(1);
  const [startCount, setStartCount] = useState(false);
  const [animateHeading, setAnimateHeading] = useState(false);
  const [offsetY, setOffsetY] = useState(0);
  const sectionRef = useRef(null);

  /* ================= SCROLL PARALLAX ================= */
  useEffect(() => {
    const handleScroll = () => setOffsetY(window.scrollY * 0.12);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* ================= INTERSECTION ================= */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStartCount(true);
          setAnimateHeading(true);
        }
      },
      { threshold: 0.35 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  /* ================= COUNTER ================= */
  useEffect(() => {
    if (!startCount) return;
    let count = 1;
    const interval = setInterval(() => {
      if (count <= 8) {
        setYears(count);
        setScale(1 + count * 0.015);
        count++;
      } else clearInterval(interval);
    }, 180);
    return () => clearInterval(interval);
  }, [startCount]);

  /* ================= MOUSE PARALLAX ================= */
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setCirclePos({
      x: (e.clientX - rect.left - rect.width / 2) / 20,
      y: (e.clientY - rect.top - rect.height / 2) / 20,
    });
  };

  const handleMouseLeave = () => setCirclePos({ x: 0, y: 0 });

  /* ================= THEME ================= */

  const bgImage = "url('/Images/HOMEPIC-NEW1.png')";
  const overlayBg = isDarkMode ? "bg-black/75" : "bg-black/60";
  const textMain = isDarkMode ? "text-white" : "text-gray-900";
  const textPara = isDarkMode ? "text-gray-300" : "text-white";

  const cardBg = isDarkMode
    ? "bg-gray-900/90 text-white"
    : "bg-white/90 text-black";

  return (
    <>
      <p className="sr-only">
        Seo Up Rank is an advanced AI powered SEO automation platform that
        provides website audits, keyword research, SEO analysis, and ranking
        optimization tools. Visit https://novarsis.ai/ to improve search engine
        rankings.
      </p>

      <section
        ref={sectionRef}
        aria-label="AI SEO automation platform overview"
        className="relative overflow-hidden py-20 sm:py-28 px-4 sm:px-6 lg:px-20 bg-fixed bg-cover bg-center"
        style={{ backgroundImage: bgImage }}
      >
        {/* Overlay */}
        <div aria-hidden="true" className={`absolute inset-0 ${overlayBg}`} />

        <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">

          {/* ================= IMAGE ================= */}

          <figure
            className="relative flex justify-center"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            {/* Glow */}
            <div
              aria-hidden="true"
              className="absolute w-52 h-52 bg-[#008300]/25 blur-3xl rounded-full transition-transform duration-500"
              style={{
                transform: `translate(${circlePos.x}px, ${circlePos.y - offsetY * 0.1}px)`
              }}
            />

            {/* IMAGE */}

            <img
              src="/Images/SmartSeoWAI.png"
              alt="Seo Up Rank AI SEO automation dashboard"
              width="420"
              height="420"
              loading="lazy"
              className="relative z-10 w-[260px] sm:w-[340px] md:w-[380px] lg:w-[420px]
              rounded-3xl shadow-2xl object-cover border border-white/20
              transition-transform duration-700"
              style={{
                transform: `translateY(${offsetY * 0.15}px) scale(${scale})`
              }}
            />

            {/* YEARS BADGE */}

            <figcaption
              className={`absolute -top-16 sm:-top-20 left-4 sm:left-6 
              rounded-2xl px-4 sm:px-6 py-3 sm:py-4
              flex items-center space-x-3 shadow-xl z-30 ${cardBg}`}
            >
              <h2 className="text-4xl sm:text-5xl font-extrabold text-[#008300]">
                {years}
              </h2>

              <div className="text-xs sm:text-sm font-semibold leading-tight">
                <p>Years</p>
                <p>SEO Innovation</p>
              </div>
            </figcaption>
          </figure>

          {/* ================= CONTENT ================= */}

          <article className={`${textMain} text-center lg:text-left`}>

            <Reveal>
              <p className="uppercase text-xs sm:text-sm font-semibold tracking-widest text-[#008300] mb-3">
                About Our SEO Tool
              </p>
            </Reveal>

            <header>
              <h2
                className={`text-3xl sm:text-4xl md:text-5xl font-extrabold mb-6 text-white leading-snug ${
                  animateHeading
                    ? "animate-reveal"
                    : "opacity-0 -translate-x-full"
                }`}
              >
                Smarter SEO with{" "}
                <span className="text-[#008300]">AI Automation</span>
              </h2>
            </header>

            <Reveal>
              <p className={`${textPara} mb-8 leading-relaxed max-w-xl`}>
                <strong>Seo Up Rank</strong> is an AI powered WordPress SEO
                automation platform that analyzes more than
                <strong> 50 SEO ranking metrics</strong> and automatically fixes
                optimization issues to improve search rankings and organic
                traffic.
              </p>
            </Reveal>

            {/* FEATURE CARDS */}

            <div className="flex flex-wrap justify-center lg:justify-start gap-6 mb-10">

              <article
                className={`flex items-center px-5 py-4 rounded-2xl w-[240px]
                backdrop-blur-xl shadow-lg hover:scale-105 transition ${cardBg}`}
              >
                <div className="text-[#008300] text-3xl mr-3">
                  <FaUserTie />
                </div>

                <div>
                  <h3 className="font-bold">AI SEO Engine</h3>
                  <p className="text-sm opacity-80">Smart Optimization</p>
                </div>
              </article>

              <article
                className={`flex items-center px-5 py-4 rounded-2xl w-[240px]
                backdrop-blur-xl shadow-lg hover:scale-105 transition ${cardBg}`}
              >
                <div className="text-[#008300] text-3xl mr-3">
                  <FaCogs />
                </div>

                <div>
                  <h3 className="font-bold">One Click Fixes</h3>
                  <p className="text-sm opacity-80">Automated Execution</p>
                </div>
              </article>

            </div>

            {/* POINTS */}

            <ul className={`space-y-3 max-w-xl ${textPara}`}>
              <li className="flex items-start">
                <FaCheckCircle className="text-[#008300] mt-1 mr-2" />
                AI powered SEO analysis using 50+ ranking metrics
              </li>

              <li className="flex items-start">
                <FaCheckCircle className="text-[#008300] mt-1 mr-2" />
                Automatic WordPress optimization with rollback safety
              </li>

              <li className="flex items-start">
                <FaCheckCircle className="text-[#008300] mt-1 mr-2" />
                Perfect for bloggers, businesses and digital agencies
              </li>
            </ul>

          </article>
        </div>

        {/* ================= HEADING ANIMATION ================= */}

        <style>{`
        @keyframes reveal{
          from{transform:translateX(-100%);opacity:0}
          to{transform:translateX(0);opacity:1}
        }

        .animate-reveal{
          animation:reveal 1.1s ease forwards;
        }
        `}</style>

      </section>
    </>
  );
};

export default HomeSectionData;